/**
 * /login — 2-step com auto-detecção de modo de auth (R44).
 *
 * Step 1: user digita email → GET /auth/discover detecta domínio
 * Step 2 muda baseado em kind:
 *   - "ldap"     → input senha + POST /auth/ldap/login + verifyOtp pra criar sessão
 *   - "oidc"     → window.location = /api/auth/sso/start/{id}
 *   - "password" → input senha + supabase.auth.signInWithPassword
 *
 * Visual (R108): card com topo em gradiente da marca + logo oficial. Fluxo intacto.
 */
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { MillerMark } from "@/components/Brand";
import { getSupabase } from "@/lib/supabase";
import { Footer } from "@/components/Footer";


type DiscoverResult = {
  kind:          "ldap" | "oidc" | "password";
  domain:        string | null;
  provider_id?:  string;
  provider_nome?: string;
};

type LdapLoginResult = {
  hashed_token: string;
  token_type:   string;
  email:        string;
  nome:         string;
};


export default function LoginPage() {
  const { t } = useTranslation();
  const router = useRouter();

  const [step,     setStep]     = useState<"email" | "password">("email");
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [loading,  setLoading]  = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [errorRaw, setErrorRaw] = useState<string | null>(null);
  const [showPw,   setShowPw]   = useState(false);

  // Estado do discover
  const [authMode, setAuthMode] = useState<DiscoverResult | null>(null);

  function clearErrors() { setErrorKey(null); setErrorRaw(null); }

  // ─── Step 1: continua com o email ──────────────────────────────────────
  async function handleContinue(e: FormEvent) {
    e.preventDefault();
    clearErrors();
    setLoading(true);
    try {
      const apiBase = process.env.NEXT_PUBLIC_API_BASE || "";
      const r = await fetch(`${apiBase}/auth/discover?email=${encodeURIComponent(email)}`);
      if (!r.ok) {
        // Se discover falhar, segue como password (não bloqueia login)
        setAuthMode({ kind: "password", domain: null });
        setStep("password");
        setLoading(false);
        return;
      }
      const data: DiscoverResult = await r.json();
      setAuthMode(data);

      if (data.kind === "oidc" && data.provider_id) {
        // Redireciona pro IdP (não passa pra step de senha)
        window.location.href = `${apiBase}/auth/sso/start/${data.provider_id}`;
        return;
      }
      // LDAP ou password → mostra campo de senha
      setStep("password");
    } catch (e: any) {
      setErrorRaw(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }

  // ─── Step 2: senha (LDAP ou Supabase) ──────────────────────────────────
  async function handleLogin(e: FormEvent) {
    e.preventDefault();
    clearErrors();
    setLoading(true);

    const supabase = getSupabase();

    try {
      if (authMode?.kind === "ldap") {
        // Login LDAP via backend → recebe magic link token → cria sessão Supabase
        const apiBase = process.env.NEXT_PUBLIC_API_BASE || "";
        const r = await fetch(`${apiBase}/auth/ldap/login`, {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ email, password }),
        });
        if (!r.ok) {
          const errJson = await r.json().catch(() => null) as { detail?: any } | null;
          handleLdapError(errJson);
          setLoading(false);
          return;
        }
        const data: LdapLoginResult = await r.json();

        // Cria sessão Supabase usando o magic link token
        const { error: vErr } = await supabase.auth.verifyOtp({
          token_hash: data.hashed_token,
          type:       "magiclink",
        });
        if (vErr) {
          setErrorRaw(`Sessão: ${vErr.message}`);
          setLoading(false);
          return;
        }
        router.push("/app");
        return;
      }

      // Fluxo Supabase normal
      const { error: err } = await supabase.auth.signInWithPassword({ email, password });
      if (err) {
        const k = translateAuthErrorKey(err.message);
        if (k) setErrorKey(k); else setErrorRaw(err.message);
        setLoading(false);
        return;
      }
      router.push("/app");
    } catch (e: any) {
      setErrorRaw(String(e?.message || e));
      setLoading(false);
    }
  }

  function handleLdapError(errJson: { detail?: any } | null) {
    const detail = errJson?.detail;
    const code   = typeof detail === "object" ? detail?.key : null;

    // Mensagens específicas pra LDAP (mais úteis pro user final)
    if (code === "errors.ldap_user_not_found" || code === "errors.ldap_user_not_provisioned") {
      setErrorKey("auth.errorLdapContactIt");  // "Entre em contato com seu TI."
    } else if (code === "errors.ldap_invalid_password") {
      setErrorKey("auth.errorInvalidCredentials");
    } else if (code === "errors.ldap_unreachable" || code === "errors.ldap_service_bind") {
      setErrorKey("auth.errorLdapDown");  // "Sistema fora do ar. Tente em alguns minutos."
    } else if (typeof detail === "object" && detail?.message) {
      setErrorRaw(detail.message);
    } else {
      setErrorRaw(String(detail ?? "Erro desconhecido"));
    }
  }

  function voltarParaEmail() {
    setStep("email");
    setPassword("");
    setShowPw(false);
    setAuthMode(null);
    clearErrors();
  }

  const subtitle = step === "email"
    ? t("auth.loginSubtitle")
    : authMode?.kind === "ldap"
      ? t("auth.loginSubtitleLdap", "Use sua senha do {{provider}} ({{domain}})", {
          provider: authMode.provider_nome || "Active Directory", domain: authMode.domain || ""
        })
      : t("auth.loginSubtitle");

  return (
    <>
      <Head><title>{t("auth.loginPageTitle")}</title></Head>

      <div
        className="min-h-screen flex flex-col items-center justify-center px-4 py-10"
        style={{ background: "var(--bg)" }}
      >
        <div className="w-full max-w-[400px] animate-slide-up">
          <div
            className="rounded-3xl overflow-hidden border border-app"
            style={{ background: "var(--surface)", boxShadow: "0 24px 60px -20px rgba(0,35,101,.28), 0 2px 8px rgba(0,0,0,.05)" }}
          >
            {/* Topo com gradiente da marca + logo oficial */}
            <div
              className="px-9 pt-9 pb-7 text-center"
              style={{ background: "linear-gradient(135deg, #002365 0%, #0b3f6b 46%, #25954b 100%)" }}
            >
              <div className="inline-flex items-center justify-center w-[60px] h-[60px] rounded-[18px] bg-white shadow-md mb-4">
                <MillerMark className="w-[38px] h-auto" />
              </div>
              <p
                className="text-[10.5px] font-semibold uppercase text-white/70 mb-2.5"
                style={{ letterSpacing: "0.28em" }}
              >
                Miller · Platform
              </p>
              <h1 className="font-serif text-[30px] font-semibold tracking-tight text-white leading-none">
                {t("auth.loginHeading")}
              </h1>
              <p className="text-sm text-white/80 mt-2">{subtitle}</p>
            </div>

            {/* Corpo com o formulário */}
            <div className="px-9 pt-7 pb-7">
              {step === "email" ? (
                <form onSubmit={handleContinue} noValidate className="space-y-4">
                  <div>
                    <label htmlFor="email" className="label">{t("auth.emailLabel")}</label>
                    <input
                      id="email" type="email" autoComplete="email" required autoFocus
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="input"
                      placeholder={t("auth.emailPlaceholder")}
                    />
                  </div>

                  {(errorKey || errorRaw) && (
                    <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3.5 py-2.5">
                      {errorKey ? t(errorKey) : errorRaw}
                    </div>
                  )}

                  <button type="submit" disabled={loading || !email} className="btn-primary btn-lg w-full">
                    {loading ? t("auth.loginCheckingDomain", "Verificando...") : t("auth.btnContinue", "Continuar")}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleLogin} noValidate className="space-y-4">
                  {/* Email mostrado como texto + botão pra voltar */}
                  <div className="flex items-center gap-2 bg-app-soft/40 border border-app rounded-xl px-3 py-2.5">
                    <span className="text-sm text-app-2 font-medium truncate flex-1">{email}</span>
                    <button
                      type="button"
                      onClick={voltarParaEmail}
                      className="text-xs text-app-3 hover:text-app-1 underline"
                    >
                      {t("auth.changeEmail", "trocar")}
                    </button>
                  </div>

                  {authMode?.kind === "ldap" && (
                    <div className="text-[11px] text-app-3 bg-blue-50/60 dark:bg-blue-950/20 border border-blue-200/50 rounded-xl px-3 py-2">
                      <span className="inline-flex items-center gap-1.5">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                        </svg>
                        {t("auth.ldapNotice", "Esta empresa usa Active Directory. Use a senha do seu AD.")}
                      </span>
                    </div>
                  )}

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label htmlFor="password" className="label !mb-0">{t("auth.passwordLabel")}</label>
                      {authMode?.kind !== "ldap" && (
                        <Link href="/auth/recover" className="text-[11px] text-app-3 hover:text-app-1">
                          {t("auth.forgotPassword")}
                        </Link>
                      )}
                    </div>
                    <div className="relative">
                      <input
                        id="password" type={showPw ? "text" : "password"} autoComplete="current-password" required autoFocus
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        className="input pr-10"
                        placeholder={t("auth.passwordPlaceholder")}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPw(v => !v)}
                        aria-label={showPw ? "Ocultar senha" : "Mostrar senha"}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-app-3 hover:text-app-1"
                      >
                        {showPw ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.243 4.243L9.88 9.88" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  {(errorKey || errorRaw) && (
                    <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3.5 py-2.5">
                      {errorKey ? t(errorKey) : errorRaw}
                    </div>
                  )}

                  <button type="submit" disabled={loading} className="btn-primary btn-lg w-full">
                    {loading ? t("auth.loginButtonLoading") : t("auth.loginButton")}
                  </button>
                </form>
              )}

              <p className="text-center text-[11.5px] text-app-3 mt-5">
                {t("auth.loginRestricted", { defaultValue: "Acesso restrito a usuários autorizados." })}
              </p>
            </div>
          </div>

          <div className="mt-5">
            <Footer variant="minimal" />
          </div>
        </div>
      </div>
    </>
  );
}


function translateAuthErrorKey(msg: string): string | null {
  const m = msg.toLowerCase();
  if (m.includes("invalid login credentials")) return "auth.errorInvalidCredentials";
  if (m.includes("email not confirmed"))      return "auth.errorEmailNotConfirmed";
  if (m.includes("user not found"))            return "auth.errorUserNotFound";
  if (m.includes("too many requests"))         return "auth.errorTooManyRequests";
  return null;
}
